<script setup lang="ts">
import { Badge, Button, Card, Icon, NumberStepper, Select } from "@/design-system";
import type { GroupKey } from "@/domain/catalogue";
import type { Plan } from "@/composables/usePlan";
import LiveMeter from "./LiveMeter.vue";

/**
 * Step 2 -- Verpflegung. One screen per leg. Block order follows
 * docs/handover/README-Verpflegung-Plan.md: live meter -> leg eyebrow ->
 * Getränke -> Gels -> Riegel -> error -> footer.
 */
const props = defineProps<{ plan: Plan }>();
const emit = defineEmits<{ next: []; back: [] }>();

const p = props.plan;

const groups = (): { key: GroupKey; title: string }[] => {
  const isSwim = p.legType.value === "swim";
  const gelsTitle = isSwim ? `${p.t.value.gels} · ${p.t.value.swimPre}` : p.t.value.gels;
  return [
    // FR-2.6: swimming drops the drinks card entirely.
    ...(isSwim ? [] : [{ key: "drinks" as const, title: p.t.value.drinks }]),
    { key: "gels" as const, title: gelsTitle },
    { key: "bars" as const, title: p.t.value.bars },
  ];
};

function optionsFor(group: GroupKey) {
  return p.catalogueFor(group).map((product) => {
    const grams = p.gramsOf(group, product.id);
    const label = p.labelFor(group, product.id);
    return { value: product.id, label: grams ? `${label}  ·  ${grams} g` : label };
  });
}

function onNext() {
  if (!p.isEmptySelection.value) emit("next");
}
</script>

<template>
  <div class="fuel">
    <LiveMeter
      :verdict="p.verdict.value"
      :eyebrow="p.isEmptySelection.value ? p.t.value.liveNothing : p.t.value.liveNow"
    />

    <p class="cw-eyebrow fuel__stage">
      {{ p.current.value.stage }} · {{ p.legIndex.value + 1 }}/{{ p.labels.value.length }}
    </p>

    <p
      v-if="p.legType.value === 'swim'"
      class="fuel__hint"
      data-testid="swim-hint"
    >
      {{ p.t.value.swimHint }}
    </p>

    <Card
      v-for="g in groups()"
      :key="g.key"
      :title="g.title"
      :data-group="g.key"
    >
      <template #action>
        <Badge tone="neutral">
          {{ p.groupTotal(g.key) }}
        </Badge>
      </template>

      <div class="fuel__lines">
        <div
          v-for="(line, i) in p.leg.value[g.key]"
          :key="`${g.key}-${i}`"
          class="fuel__line"
          data-testid="line"
        >
          <Select
            :model-value="line.id"
            :options="optionsFor(g.key)"
            @update:model-value="(id) => p.setLineProduct(g.key, i, id)"
          />
          <div class="fuel__line-right">
            <NumberStepper
              :model-value="line.count"
              :step="1"
              :min="0"
              :max="30"
              :data-count="`${g.key}-${i}`"
              @update:model-value="(v: number) => p.setLineCount(g.key, i, v)"
            />
            <button
              v-if="p.canRemoveLine(g.key)"
              type="button"
              class="fuel__trash"
              :aria-label="p.t.value.del"
              :data-remove="`${g.key}-${i}`"
              @click="p.removeLine(g.key, i)"
            >
              <Icon
                name="trash-2"
                :size="17"
              />
            </button>
          </div>
        </div>

        <Button
          v-if="p.canAddLine(g.key)"
          variant="ghost"
          size="sm"
          icon="plus"
          class="fuel__add"
          :data-add="g.key"
          @click="p.addLine(g.key)"
        >
          {{ p.t.value.addLine }}
        </Button>
      </div>
    </Card>

    <p
      v-if="p.isEmptySelection.value"
      class="fuel__error"
      data-testid="empty-error"
    >
      {{ p.t.value.emptyProducts }}
    </p>

    <div class="fuel__footer app-actions">
      <Button
        variant="outline"
        size="lg"
        icon="arrow-left"
        data-testid="fuel-back"
        @click="emit('back')"
      >
        {{ p.t.value.back }}
      </Button>
      <Button
        size="lg"
        block
        icon-after="arrow-right"
        :disabled="p.isEmptySelection.value"
        data-testid="fuel-next"
        @click="onNext"
      >
        {{ p.isLastLeg.value ? p.t.value.calcCta : p.nextLabel.value }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.fuel {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}
.fuel__stage {
  margin: 0;
}
.fuel__hint {
  margin: 0;
  font: var(--type-body-sm);
  color: var(--text-muted);
}
.fuel__lines {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}
.fuel__line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-5);
  align-items: center;
}
.fuel__line-right {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}
.fuel__trash {
  border: 0;
  background: none;
  color: var(--text-faint);
  cursor: pointer;
  display: flex;
  padding: var(--space-2);
}
.fuel__trash:hover {
  color: var(--danger);
}
.fuel__add {
  align-self: flex-start;
}
.fuel__error {
  margin: 0;
  font: var(--type-body-sm);
  color: var(--danger);
}
.fuel__footer {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: var(--space-5);
}
</style>
